import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Button, Input, Select, Tag, Space, Modal, Form,
  message, Drawer, Typography, Descriptions, Timeline, Avatar,
} from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import { customerApi, logApi, userApi, getApiBaseURL } from '../../services/api';
import useAuthStore from '../../store/authStore';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

const STAGES = ['新客户', '意向客户', '跟进中', '报价阶段', '已成交', '已流失'];
const STAGE_COLORS = {
  '新客户': 'blue', '意向客户': 'orange', '跟进中': 'gold',
  '报价阶段': 'purple', '已成交': 'green', '已流失': 'red',
};
const LOG_TYPES = ['电话', '微信', '拜访', '报价', '成交', '备注'];

export default function Customers() {
  const user = useAuthStore((s) => s.user);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState({ page: 1, pageSize: 10, q: '', stage: '' });

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeCustomer, setActiveCustomer] = useState(null);
  const [customerLogs, setCustomerLogs] = useState([]);
  const [logForm] = Form.useForm();
  const [logLoading, setLogLoading] = useState(false);

  const [salesList, setSalesList] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await customerApi.list(query);
      setData(res.data);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    if (user?.role === 'admin') {
      userApi.list().then(setSalesList).catch(() => {});
    }
  }, [user]);

  const openDetail = async (record) => {
    setActiveCustomer(record);
    setDrawerOpen(true);
    setCustomerLogs([]);
    try {
      // 获取客户详情（含跟进记录）
      const customer = await customerApi.get(record.id);
      setActiveCustomer(customer);
      setCustomerLogs(customer.logs || []);
    } catch (e) {
      message.error(e?.error || '加载客户详情失败');
      setDrawerOpen(false);
    }
  };

  const handleSave = async (values) => {
    try {
      if (editing) {
        await customerApi.update(editing.id, values);
        message.success('更新成功');
      } else {
        await customerApi.create(values);
        message.success('客户已添加');
      }
      setModalOpen(false);
      form.resetFields();
      setEditing(null);
      fetchData();
    } catch (e) {
      message.error(e?.error || '操作失败');
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: '确认删除该客户？',
      content: '删除后所有跟进记录也将一并删除，操作不可恢复。',
      okText: '确认删除', okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        await customerApi.delete(id);
        message.success('已删除');
        fetchData();
      },
    });
  };

  const addLog = async (values) => {
    setLogLoading(true);
    try {
      await logApi.create({ ...values, customerId: activeCustomer.id });
      message.success('跟进记录已添加');
      logForm.resetFields();
      const res = await logApi.list({ customerId: activeCustomer.id, pageSize: 50 });
      setCustomerLogs(res.data);
      // 同步更新阶段
      if (values.stage) {
        await customerApi.update(activeCustomer.id, { stage: values.stage });
        fetchData();
      }
    } finally {
      setLogLoading(false);
    }
  };

  const columns = [
    {
      title: '客户', dataIndex: 'name', key: 'name',
      render: (name, r) => (
        <Space>
          <Avatar style={{ background: '#1890ff', flexShrink: 0 }}>{name[0]}</Avatar>
          <span>
            <a onClick={() => openDetail(r)} style={{ fontWeight: 500 }}>{name}</a>
            {r.company && <div style={{ fontSize: 12, color: '#888' }}>{r.company}</div>}
          </span>
        </Space>
      ),
    },
    { title: '手机号', dataIndex: 'phone', key: 'phone', render: (v) => v || '—' },
    {
      title: '阶段', dataIndex: 'stage', key: 'stage',
      render: (v) => <Tag color={STAGE_COLORS[v] || 'default'}>{v}</Tag>,
    },
    { title: '来源', dataIndex: 'source', key: 'source' },
    {
      title: '负责销售', dataIndex: 'owner', key: 'owner',
      render: (o) => o?.name || '—',
    },
    {
      title: '添加时间', dataIndex: 'createdAt', key: 'createdAt',
      render: (v) => dayjs(v).format('MM-DD HH:mm'),
    },
    {
      title: '操作', key: 'actions',
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => {
            setEditing(r); form.setFieldsValue(r); setModalOpen(true);
          }}>编辑</Button>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(r.id)}>删除</Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>客户管理</Title>
        <Space>
          <Button icon={<DownloadOutlined />} onClick={() => window.open(getApiBaseURL() + '/exports/customers', '_blank')}>导出 CSV</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditing(null); form.resetFields(); setModalOpen(true); }}
            style={{ background: '#07c160', borderColor: '#07c160' }}>新增客户</Button>
        </Space>
      </div>

      <Space style={{ marginBottom: 16 }}>
        <Input.Search placeholder="搜索姓名/手机/公司" allowClear style={{ width: 220 }}
          onSearch={(v) => setQuery((q) => ({ ...q, q: v, page: 1 }))} />
        <Select placeholder="筛选阶段" allowClear style={{ width: 140 }}
          onChange={(v) => setQuery((q) => ({ ...q, stage: v || '', page: 1 }))}>
          {STAGES.map((s) => <Option key={s} value={s}><Tag color={STAGE_COLORS[s]}>{s}</Tag></Option>)}
        </Select>
      </Space>

      <Table
        columns={columns} dataSource={data} rowKey="id" loading={loading}
        pagination={{
          total, current: query.page, pageSize: query.pageSize, showTotal: (t) => `共 ${t} 位客户`,
          onChange: (page) => setQuery((q) => ({ ...q, page })),
        }}
        style={{ borderRadius: 12, overflow: 'hidden' }}
      />

      {/* 新增/编辑弹窗 */}
      <Modal title={editing ? '编辑客户' : '新增客户'} open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditing(null); form.resetFields(); }}
        onOk={() => form.submit()} okText="保存">
        <Form form={form} layout="vertical" onFinish={handleSave} style={{ marginTop: 16 }}>
          <Form.Item name="name" label="姓名" rules={[{ required: true }]}>
            <Input placeholder="客户姓名" />
          </Form.Item>
          <Form.Item name="phone" label="手机号">
            <Input placeholder="手机号码" />
          </Form.Item>
          <Form.Item name="company" label="公司">
            <Input placeholder="公司名称" />
          </Form.Item>
          <Form.Item name="position" label="职位">
            <Input placeholder="职位" />
          </Form.Item>
          <Form.Item name="wechat" label="微信号">
            <Input placeholder="微信号" />
          </Form.Item>
          <Form.Item name="stage" label="阶段" initialValue="新客户">
            <Select>{STAGES.map((s) => <Option key={s} value={s}>{s}</Option>)}</Select>
          </Form.Item>
          {user?.role === 'admin' && (
            <Form.Item name="ownerId" label="负责销售">
              <Select placeholder="选择负责人">
                {salesList.map((u) => <Option key={u.id} value={u.id}>{u.name}</Option>)}
              </Select>
            </Form.Item>
          )}
          <Form.Item name="notes" label="备注">
            <Input.TextArea rows={3} placeholder="客户备注" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 客户详情侧抽屉 */}
      <Drawer title="客户详情" open={drawerOpen} onClose={() => setDrawerOpen(false)} width={480}>
        {activeCustomer && (
          <>
            <Space style={{ marginBottom: 20 }}>
              <Avatar size={48} style={{ background: '#1890ff', fontSize: 20 }}>{activeCustomer.name[0]}</Avatar>
              <div>
                <div style={{ fontWeight: 500, fontSize: 16 }}>{activeCustomer.name}</div>
                <Text type="secondary">{activeCustomer.company} {activeCustomer.position}</Text>
              </div>
            </Space>
            <Descriptions column={1} size="small" bordered style={{ marginBottom: 20 }}>
              <Descriptions.Item label="手机">{activeCustomer.phone || '—'}</Descriptions.Item>
              <Descriptions.Item label="微信">{activeCustomer.wechat || '—'}</Descriptions.Item>
              <Descriptions.Item label="来源">{activeCustomer.source}</Descriptions.Item>
              <Descriptions.Item label="阶段"><Tag color={STAGE_COLORS[activeCustomer.stage]}>{activeCustomer.stage}</Tag></Descriptions.Item>
              <Descriptions.Item label="负责销售">{activeCustomer.owner?.name}</Descriptions.Item>
              <Descriptions.Item label="备注">{activeCustomer.notes || '—'}</Descriptions.Item>
            </Descriptions>

            <Title level={5}>添加跟进记录</Title>
            <Form form={logForm} layout="vertical" onFinish={addLog} style={{ marginBottom: 20 }}>
              <Form.Item name="type" label="类型" rules={[{ required: true }]}>
                <Select placeholder="选择跟进方式">
                  {LOG_TYPES.map((t) => <Option key={t} value={t}>{t}</Option>)}
                </Select>
              </Form.Item>
              <Form.Item name="content" label="内容" rules={[{ required: true }]}>
                <Input.TextArea rows={3} placeholder="记录本次沟通内容..." />
              </Form.Item>
              <Form.Item name="stage" label="更新阶段（可选）">
                <Select allowClear placeholder="更新客户阶段">
                  {STAGES.map((s) => <Option key={s} value={s}>{s}</Option>)}
                </Select>
              </Form.Item>
              <Form.Item name="nextAction" label="下次计划（可选）">
                <Input placeholder="下次跟进计划" />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={logLoading}
                style={{ background: '#07c160', borderColor: '#07c160' }}>添加记录</Button>
            </Form>

            <Title level={5}>跟进记录</Title>
            <Timeline
              items={customerLogs.map((log) => ({
                color: log.type === '成交' ? 'green' : 'blue',
                children: (
                  <div>
                    <Space>
                      <Tag color={log.type === '成交' ? 'success' : 'blue'}>{log.type}</Tag>
                      <Text type="secondary" style={{ fontSize: 12 }}>{log.user?.name}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>{dayjs(log.createdAt).format('MM-DD HH:mm')}</Text>
                    </Space>
                    <div style={{ marginTop: 4 }}>{log.content}</div>
                    {log.nextAction && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>下次：{log.nextAction}</div>}
                  </div>
                ),
              }))}
            />
          </>
        )}
      </Drawer>
    </div>
  );
}
